import { useState, useEffect } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";

export function QaReportsViewModel(logout) {
  const defaultFilters = {
    class: { value: -1, label: "All classes" },
    category: { value: -1, label: "All categories" },
    product: { value: -1, label: "All products" },
    supplier: { value: -1, label: "All suppliers" },
    startDate: null,
    endDate: null,
  };
  const qaReportOptions = [
    { value: 5, label: "Deleted Cartons" },
    { value: 6, label: "Inventory Valuation" },
    { value: 8, label: "Flow Reports" },
    { value: 7, label: "Open WIPs Report" },
    { value: 9, label: "Production Detail" },
    { value: 10, label: "Production Summary" },
    { value: 1, label: "Receiving Details" },
    { value: 2, label: "Receiving Summary" },
    { value: 3, label: "Reorder Limit Items" },
    { value: 11, label: "Sales Comparison Report" },
    { value: 4, label: "Sales Report by PLU" },
  ];
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState({
    value: -1,
    label: "Select report",
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message={message} />,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getCategoryTypes = async function () {
    if (filters.class.value === -1) {
      return;
    }
    let callResult = await makeGetApiCall(
      `categoriesForDropdownWithClass/${filters.class.value}`
    );
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
    }
  };

  useEffect(() => {
    if (filters.class.value === -1) {
      setCategories([{ value: -1, label: "All categories" }]);
    } else {
      getCategoryTypes();
    }
  }, [filters.class.value]);

  const getClassTypes = async function () {
    let callResult = await makeGetApiCall("classesForDropdown");
    if (callResult.success) {
      setClasses([{ value: -1, label: "All classes" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers([{ value: -1, label: "All customers" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProducts([{ value: -1, label: "All products" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSuppliers([{ value: -1, label: "All suppliers" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const printSelectedReport = async function () {
    showProgressModal("Generating report...");
    const reportUrl = (() => {
      switch (reportType.value) {
        case 1:
          return "generateGeneralReceivingDetailsReport";
        case 2:
          return "generateGeneralReceivingSummaryReport";
        case 3:
          return "reorderLimitReport";
        case 4:
          return "salesReportByPlu";
        case 5:
          return "deletedCartonsReport";
        case 6:
          return "inventoryValuationReport";
        case 7:
          return "openWipsReport";
        case 8:
          return "inventoryFlow";
        case 9:
          return "productionDetailsReport";
        case 10:
          return "productionSummaryReport";
        case 11:
          return "salesComparisonReport";
        default:
          return "error_type";
      }
    })();

    let callResult = await makePostApiCall(reportUrl, {
      filter: {
        ...filters,
        endDate: reportType.value === 6 ? filters.startDate : filters.endDate,
      },
    });

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
      setEmptyModelOpen({ open: false, childComponent: null });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal(callResult.data.message);
    }

    return callResult;
  };

  function resetFilters() {
    setFilters(defaultFilters);
  }
  return {
    emptyModal,
    loading,
    setEmptyModelOpen,
    setShowSimpleNotification,
    showSimpleNotification,
    filters,
    printSelectedReport,
    qaReportOptions,
    setFilters,
    reportType,
    setReportType,
    resetFilters,
    categories,
    products,
    suppliers,
    getCategoryTypes,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getSupplierOptionsForDropdown,
    classes,
    getClassTypes,
  };
}
